import React from "react";
import { Warning } from "@phosphor-icons/react";
import { money } from "@/lib/format";
import { untiedYear } from "@/lib/untiedWithdrawals";
import { withdrawalPhrase } from "@/lib/withdrawalNames";

const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const andList = (xs) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

/** The one line for untied money in other tax years. (ZEN-790) */
export function otherYearsSentence(otherYears) {
  const n = otherYears.reduce((t, y) => t + y.count, 0);
  const years = andList(otherYears.map((y) => String(y.year)));
  return `${n} withdrawal${n === 1 ? "" : "s"} from ${years} still need${n === 1 ? "s" : ""} tying to expenses`;
}

/** One box per withdrawal holding money tied to no expense. Wording approved
 *  by Maryse on 17 Oct 2026 (ZEN-785). `onTie(w)` opens that withdrawal.
 *  `otherYears` ([{year, count}]) adds one line for untied money in other tax
 *  years, each year a link to it (`onSeeYear`). (ZEN-790)
 *
 *  `plan` ({ ids, sentence, withdrawLabel, onWithdraw }) says what is left to
 *  withdraw once that money is tied (ZEN-791; sentences approved by Maryse,
 *  21 Oct 2026). With one such withdrawal its box carries the sentence in place
 *  of its usual one; with several, the boxes stay as they are and the sentence
 *  follows them once. */
export function UntiedWithdrawalReminder({ withdrawals = [], onTie, otherYears = [], onSeeYear, plan = null, testid = "untied-reminder" }) {
  if (!withdrawals.length && !otherYears.length) return null;
  const planIds = plan ? new Set(plan.ids) : new Set();
  const inBox = !!plan && planIds.size === 1 && withdrawals.some((w) => planIds.has(w.id));
  const withdrawLink = plan?.withdrawLabel && plan.onWithdraw && (
    <button type="button" onClick={plan.onWithdraw} className="font-semibold underline hover:text-amber-900"
      data-testid={`${testid}-withdraw-left`}>
      {plan.withdrawLabel}
    </button>
  );
  return (
    <div className="space-y-2" data-testid={testid}>
      {withdrawals.map((w) => {
        const year = untiedYear(w);
        const planned = inBox && planIds.has(w.id);
        return (
          <div key={w.id} className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800"
            data-testid={`${testid}-${w.id}`}>
            <Warning size={16} weight="bold" className="mt-0.5 shrink-0" aria-hidden="true" />
            <div className="text-xs leading-relaxed">
              <div className="font-semibold">Already withdrawn, not tied to any expense · {money(w.untied_usd, "USD")}</div>
              <div data-testid={planned ? `${testid}-plan` : undefined}>
                {planned ? plan.sentence
                  : <>{capital(`from ${withdrawalPhrase(w)}.`)} Tie it to one or more{year ? ` ${year}` : ""} expenses before withdrawing more money.</>}
              </div>
              <div className="flex flex-wrap gap-x-3">
                {onTie && (
                  <button type="button" onClick={() => onTie(w)} className="font-semibold underline hover:text-amber-900"
                    data-testid={`${testid}-tie-${w.id}`}>
                    Tie it to expense(s) →
                  </button>
                )}
                {planned && withdrawLink}
              </div>
            </div>
          </div>
        );
      })}
      {plan && !inBox && (
        <div className="flex flex-wrap items-center gap-x-3 text-xs text-amber-800" data-testid={`${testid}-plan`}>
          <span>{plan.sentence}</span>
          {withdrawLink}
        </div>
      )}
      {otherYears.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-amber-800" data-testid={`${testid}-other-years`}>
          <Warning size={14} weight="bold" className="shrink-0" aria-hidden="true" />
          <span>{otherYearsSentence(otherYears)}.</span>
          {otherYears.map((y, i) => (
            <React.Fragment key={y.year}>
              {i > 0 && <span>·</span>}
              <button type="button" onClick={() => onSeeYear?.(y.year)} className="font-semibold underline hover:text-amber-900"
                data-testid={`${testid}-see-${y.year}`}>
                Switch to {y.year} →
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
